import React, { forwardRef } from "react";
import PrintHeader from "./PrintHeader";
import { Footer } from "./PrintFooter";
import { formatDate } from "../../utils/dateUtils";

const styles = {
  container: {
    background: "#fff",
    color: "#000",
    width: "210mm",
    height: "297mm",
    margin: "0 auto",
    // padding: "10mm",
    fontFamily: "Arial, sans-serif",
    fontSize: "15px",
    lineHeight: "1.4",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    // border: "1px solid",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    fontWeight: "bold",
    fontSize: "16px",
  },
  sectionTitle: {
    fontWeight: "bold",
    margin: "10px 0 4px",
  },
  gridRow: {
    display: "grid",
    gridTemplateColumns: "20% 30% 20% 30%",
    marginBottom: "4px",
    alignItems: "start",
  },
  gridRowBiometry: {
    display: "grid",
    gridTemplateColumns: "40% 15% 15% 30%",
    marginBottom: "4px",
    alignItems: "start",
  },
  gridRowAmniotic: {
    display: "grid",
    gridTemplateColumns: "20% 15% 15% 15% 15% 15%",
    marginBottom: "4px",
    alignItems: "start",
  },
  gridRowEnd: {
    display: "grid",
    gridTemplateColumns: "20% 30% 20% 30%",
    marginBottom: "20px",
    alignItems: "start",
  },
  gridRowSummary: {
    display: "grid",
    gridTemplateColumns: "40% 40% 20% 10%",
    marginBottom: "4px",
    alignItems: "start",
  },
  th: {
    fontWeight: "bold",
    fontSize: "15px",
    verticalAlign: "top",
  },
  td: {
    fontSize: "15px",
    textAlign: "left",
    verticalAlign: "top",
  },
  footer: {
    marginTop: "auto",
  },
  signature: {
    textAlign: "right",
    marginTop: "40px",
  },
  sigBlock: {
    display: "inline-block",
    textAlign: "center",
    minWidth: "200px",
  },
  sigLine: {
    borderTop: "1px solid #000",
    marginBottom: "0px",
  },
  disclaimer: {
    fontSize: "10px",
    fontStyle: "italic",
    marginTop: "20px",
    textAlign: "justify",
  },
};

const UltrasoundBiophysicalPrint = forwardRef(
  ({ data = {}, patient = {} }, ref) => {
    return (
      <div ref={ref} style={styles.container}>
        <div>
          <PrintHeader />

          <div style={styles.titleRow}>
            <div>Examination: {data.type?.toUpperCase()}</div>
            <div>Date: {data.date}</div>
          </div>

          {/* PATIENT DATA */}
          <div style={styles.sectionTitle}>PATIENT DATA</div>
          <div style={styles.gridRow}>
            <div style={styles.th}>Name:</div>
            <div style={styles.td}>{patient?.name}</div>
            <div style={styles.th}>Date of Birth:</div>
            <div style={styles.td}>{formatDate(patient?.date_of_birth)}</div>
          </div>
          <div style={styles.gridRow}>
            <div style={styles.th}>Address:</div>
            <div style={styles.td}>{patient?.address}</div>
            <div style={styles.th}>Age:</div>
            <div style={styles.td}>{patient?.age} years old</div>
          </div>
          <div style={styles.gridRow}>
            <div style={styles.th}>G/P:</div>
            <div style={styles.td}>{data.ob_data?.gravida_para}</div>
            <div style={styles.th}>AOG:</div>
            <div style={styles.td}>{data.ob_data?.aog}</div>
          </div>
          <div style={styles.gridRowEnd}>
            <div style={styles.th}>LMP:</div>
            <div style={styles.td}>{formatDate(data.ob_data?.lmp)}</div>
            <div style={styles.th}>EDD:</div>
            <div style={styles.td}>{formatDate(data.ob_data?.edd)}</div>
          </div>

          {/* FETAL SURVEY */}
          <div style={styles.sectionTitle}>FETAL SURVEY</div>
          <div style={styles.gridRow}>
            <div style={styles.th}>Presentation:</div>
            <div style={styles.td}>{data.fetal_survey?.presentation}</div>
            <div style={styles.th}>Fetal Sex:</div>
            <div style={styles.td}>{data.fetal_survey?.fetal_sex}</div>
          </div>
          <div style={styles.gridRow}>
            <div style={styles.th}>No. of Fetus:</div>
            <div style={styles.td}>{data.fetal_survey?.number_of_fetus}</div>
            <div style={styles.th}>Placental Grade:</div>
            <div style={styles.td}>{data.fetal_survey?.placenta_grade}</div>
          </div>
          <div style={styles.gridRow}>
            <div style={styles.th}>Fetal Heart Beat:</div>
            <div style={styles.td}>{data.fetal_survey?.fetal_heart_rate}</div>
            <div style={styles.th}>Placental Position:</div>
            <div style={styles.td}>{data.fetal_survey?.placenta_position}</div>
          </div>
          <div style={styles.gridRowAmniotic}>
            <div style={styles.th}>Amniotic Fluid:</div>
            <div style={styles.td}>
              <div>Q1: {data.fetal_survey?.amniotic_fluid?.afi.q1}</div>
              <div>Q3: {data.fetal_survey?.amniotic_fluid?.afi.q3}</div>
            </div>
            <div style={styles.td}>
              <div>Q2: {data.fetal_survey?.amniotic_fluid?.afi.q2}</div>
              <div>Q4: {data.fetal_survey?.amniotic_fluid?.afi.q4}</div>
            </div>
          </div>
          <div style={styles.gridRowEnd}>
            {/* {data.type === "Biophysical Score" ? ( */}
            <div style={styles.th}>Total AFI:</div>
            <div style={styles.td}>
              {data.fetal_survey?.amniotic_fluid?.afi.total}
            </div>

            <div style={styles.th}>SVP:</div>
            <div style={styles.td}>
              {data.fetal_survey?.amniotic_fluid?.svp}
            </div>
          </div>

          {/* FETAL BIOMETRY */}
          <div style={styles.sectionTitle}>FETAL BIOMETRY</div>
          <div style={{ marginBottom: "15px" }}>
            {[
              { label: "Biparietal Diameter (BPD)", key: "bpd" },
              { label: "Occipito-Frontal Diameter (OFD)", key: "ofd" },
              { label: "Head Circumference (HC)", key: "hc" },
              { label: "Abdominal Circumference (AC)", key: "ac" },
              { label: "Femur Length (FL)", key: "fl" },
            ].map(({ label, key }) => (
              <div style={styles.gridRowBiometry} key={key}>
                <div style={styles.th}>{label}:</div>
                <div style={styles.td}>
                  {data.fetal_biometry?.[key]?.measurement}
                </div>
                <div style={styles.td}>
                  {data.fetal_biometry?.[key]?.age_equiv}
                </div>
              </div>
            ))}
          </div>

          {/* BIOPHYSICAL PROFILE */}
          {data.type === "Biophysical Score" && (
            <>
              <div style={styles.sectionTitle}>BIOPHYSICAL PROFILE</div>
              <div style={styles.gridRow}>
                <div style={styles.th}>Fetal Breathing:</div>
                <div style={styles.td}>
                  {data.biophysical_profile?.fetal_breathing}
                </div>
                <div style={styles.th}>Fetal Tone:</div>
                <div style={styles.td}>
                  {data.biophysical_profile?.fetal_tone}
                </div>
              </div>
              <div style={styles.gridRow}>
                <div style={styles.th}>Fetal Movement:</div>
                <div style={styles.td}>
                  {data.biophysical_profile?.fetal_movement}
                </div>
                <div style={styles.th}>AFI:</div>
                <div style={styles.td}>
                  {data.biophysical_profile?.afi_score}
                </div>
              </div>
              <div style={styles.gridRowEnd}>
                <div style={styles.th}>Total:</div>
                <div style={styles.td}>{data.biophysical_profile?.total}</div>
                <div></div>
                <div></div>
              </div>
            </>
          )}

          {/* SUMMARY */}
          <div style={styles.gridRowSummary}>
            <div style={styles.th}>AVERAGE ULTRASONIC AGE:</div>
            <div style={styles.td}>{data.average_age}</div>
            <div></div>
            <div></div>
          </div>
          <div style={styles.gridRowSummary}>
            <div style={styles.th}>ULTRASONIC EDD:</div>
            <div style={styles.td}>{formatDate(data.ultrasonic_edd)}</div>
            <div></div>
            <div></div>
          </div>
          <div style={styles.gridRowSummary}>
            <div style={styles.th}>ESTIMATED FETAL WEIGHT (EFW):</div>
            <div style={styles.td}>{data.efw}</div>
            <div></div>
            <div></div>
          </div>

          {/* Others & Impression */}
          <div style={styles.sectionTitle}>OTHERS:</div>
          <p style={{ whiteSpace: "pre-line" }}>{data.others}</p>
          <div style={styles.sectionTitle}>IMPRESSION:</div>
          <p style={{ whiteSpace: "pre-line" }}>{data.impression}</p>
        </div>

        {/* Footer */}
        <Footer radiologist={data.radiologist} />
      </div>
    );
  }
);

export default UltrasoundBiophysicalPrint;
